// Helpers for one-source runtime compile/apply flow.
// They keep the main compile-entry file focused on orchestration.
import { getNow } from '../context-time'
import { ensureRuntimeFeatures, getRuntimeFeature } from '../../features'
import type { LuisCompileDebugRule } from '@lentystyle/compiler'
import type { LuisLazyFeatureApi, LuisObservedFeatureApi, LuisRuntimeTimingBreakdown } from '../../features'

// Ensure observed runtime bundle exists when compiled output contains observed rules.
export async function ensureObservedFeatureForCompile(
  observedRuleCount: number,
  initialObservedRuleCount: number,
  lazyEnabled: boolean,
): Promise<{
  observedFeature: LuisObservedFeatureApi | undefined
  lazyFeature: LuisLazyFeatureApi | undefined
  featureLoadDurationMs?: number
}> {
  let observedFeature = getRuntimeFeature('observed')
  let lazyFeature = lazyEnabled ? getRuntimeFeature('lazy') : undefined
  const requiresObservedFeature = observedRuleCount > 0 || (lazyEnabled && initialObservedRuleCount > 0)
  const requiredFeatures = new Set<'observed' | 'lazy'>()
  // Queue observed runtime feature only when lazy/observed output needs it.
  if (requiresObservedFeature && !observedFeature) {
    requiredFeatures.add('observed')
  } else {
    // Skip observed feature load when current compile does not need it.
  }
  // Queue lazy runtime feature only when source opted into lazy mode.
  if (lazyEnabled && !lazyFeature) {
    requiredFeatures.add('lazy')
  } else {
    // Skip lazy feature load when viewport-aware observed mode is disabled.
  }

  if (requiredFeatures.size === 0) {
    return { observedFeature, lazyFeature }
  } else {
    // Continue observed/lazy runtime feature load for current source.
  }

  const featureLoadStart = getNow()
  await ensureRuntimeFeatures(requiredFeatures)
  observedFeature = getRuntimeFeature('observed')
  lazyFeature = lazyEnabled ? getRuntimeFeature('lazy') : undefined
  const featureLoadDurationMs = getNow() - featureLoadStart
  // Return observed/lazy feature handles plus load timing for perf logs.
  return {
    observedFeature,
    lazyFeature,
    featureLoadDurationMs: featureLoadDurationMs > 0 ? featureLoadDurationMs : undefined,
  }
}

// Check whether async compile result still belongs to latest rebuild cycle.
export function isBuildRevisionCurrent(buildRevision: number): boolean {
  // Read current revision marker written by the rebuild orchestrator.
  return (window as Window & { __LentyStyleActiveBuildRevision?: number }).__LentyStyleActiveBuildRevision === buildRevision
}

// Remove debug map and observed state when source compile fails.
export function clearDebugAndObservedState(styleId: string): void {
  getRuntimeFeature('debug')?.clearDebugRuleMap(styleId)
  const observedFeature = getRuntimeFeature('observed')
  if (!observedFeature) {
    document.getElementById(`${styleId}-observed`)?.remove()
  } else {
    observedFeature.removeObservedRules(styleId)
  }
}

// Merge compile/apply/feature/debug-map timings into one perf payload.
export function buildPublishTiming(
  compile: {
    compileDurationMs: number
    execution: 'main' | 'worker'
    timing?: Partial<LuisRuntimeTimingBreakdown>
  },
  applyDurationMs: number,
  totalDurationMs: number,
  featureLoadDurationMs: number | undefined,
  preparedCssTiming: {
    remapDurationMs?: number
    composeDurationMs?: number
    mapDurationMs?: number
  },
): LuisRuntimeTimingBreakdown {
  // Return merged runtime timing breakdown for performance logging.
  return {
    compileDurationMs: compile.compileDurationMs,
    totalDurationMs,
    applyDurationMs,
    ...compile.timing,
    featureLoadDurationMs: featureLoadDurationMs ?? compile.timing?.featureLoadDurationMs,
    ...preparedCssTiming,
  }
}
